import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Edit,
  Upload,
  Sparkles,
  Loader2,
  Eye,
  EyeOff,
  Search,
  X,
  BookOpen,
  Salad,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const TYPE_META = {
  feature: { label: "おすすめ商品の特集", icon: Sparkles, color: "text-emerald-600", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  nutrition: { label: "栄養士向けお役立ち情報", icon: Salad, color: "text-sky-600", badge: "bg-sky-50 text-sky-700 border-sky-200" },
};

const emptyForm = {
  type: "feature",
  title: "",
  body: "",
  keywords: "",
  product_ids: [],
  cover_image_url: "",
  is_published: true,
  sort_order: 0,
};

export default function ArticlesManagement() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("feature");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // product picker
  const [prodQuery, setProdQuery] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]); // {id,name,unit}
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/articles`, getAuthHeaders());
      setArticles(res.data || []);
    } catch (e) {
      toast.error("記事の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  // debounced product search
  useEffect(() => {
    if (form.type !== "feature") return;
    const q = prodQuery.trim();
    if (!q) {
      setProdResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(
          `${API}/products/paginated?search=${encodeURIComponent(q)}&page_size=20`,
          getAuthHeaders()
        );
        setProdResults(res.data?.items || []);
      } catch (e) {
        setProdResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [prodQuery, form.type]);

  const openCreate = (type) => {
    setEditing(null);
    setForm({ ...emptyForm, type });
    setSelectedProducts([]);
    setProdQuery("");
    setProdResults([]);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      type: a.type,
      title: a.title || "",
      body: a.body || "",
      keywords: a.keywords || "",
      product_ids: a.product_ids || [],
      cover_image_url: a.cover_image_url || "",
      is_published: a.is_published !== false,
      sort_order: a.sort_order || 0,
    });
    setSelectedProducts((a.products || []).map((p) => ({ id: p.id, name: p.name, unit: p.unit })));
    setProdQuery("");
    setProdResults([]);
    setDialogOpen(true);
  };

  const toggleProduct = (p) => {
    const exists = selectedProducts.find((x) => x.id === p.id);
    let next;
    if (exists) next = selectedProducts.filter((x) => x.id !== p.id);
    else next = [...selectedProducts, { id: p.id, name: p.name, unit: p.unit }];
    setSelectedProducts(next);
    setForm((f) => ({ ...f, product_ids: next.map((x) => x.id) }));
  };

  const handleGenerate = async () => {
    if (!form.keywords.trim()) {
      toast.error("キーワードを入力してください");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/articles/generate`,
        {
          type: form.type,
          keywords: form.keywords.trim(),
          product_ids: form.type === "feature" ? form.product_ids : [],
        },
        { ...getAuthHeaders(), timeout: 120000 }
      );
      setForm((f) => ({ ...f, title: res.data.title || f.title, body: res.data.body || f.body }));
      toast.success("AIが記事を生成しました。内容を確認・編集して保存してください");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI生成に失敗しました");
    } finally {
      setGenerating(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/articles/upload-image`, fd, {
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image_url: res.data.image_url }));
      toast.success("画像をアップロードしました");
    } catch (err) {
      toast.error("画像アップロードに失敗しました");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("タイトルと本文を入力してください");
      return;
    }
    try {
      const payload = { ...form, product_ids: form.type === "feature" ? form.product_ids : [] };
      if (editing) {
        await axios.put(`${API}/admin/articles/${editing.id}`, payload, getAuthHeaders());
        toast.success("記事を更新しました");
      } else {
        await axios.post(`${API}/admin/articles`, payload, getAuthHeaders());
        toast.success("記事を作成しました");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "保存に失敗しました");
    }
  };

  const togglePublish = async (a) => {
    try {
      await axios.put(`${API}/admin/articles/${a.id}`, { is_published: !a.is_published }, getAuthHeaders());
      fetchAll();
    } catch (e) {
      toast.error("更新に失敗しました");
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`「${a.title}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/admin/articles/${a.id}`, getAuthHeaders());
      toast.success("削除しました");
      fetchAll();
    } catch (e) {
      toast.error("削除に失敗しました");
    }
  };

  const filtered = articles.filter((a) => a.type === tab);

  return (
    <div className="space-y-6" data-testid="articles-management">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-7 bg-emerald-600 rounded-full" />
            <h1 className="text-2xl font-bold text-slate-800">特集・お役立ち情報</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            おすすめ商品の特集や、栄養士の先生向けのお役立ち情報を掲載できます。キーワードからAIで記事を生成できます。
          </p>
        </div>
        <Button
          onClick={() => openCreate(tab)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="article-new-btn"
        >
          <Plus className="h-4 w-4 mr-1" />新しい記事
        </Button>
      </div>

      {/* type tabs */}
      <div className="flex gap-2">
        {Object.entries(TYPE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const active = tab === key;
          const count = articles.filter((a) => a.type === key).length;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`article-tab-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                active ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Icon className="h-4 w-4" />
              {meta.label}
              <span className={`text-[11px] px-1.5 rounded ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-10 text-center">読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-400 text-sm py-10 text-center border border-dashed border-slate-200 rounded-lg" data-testid="articles-empty">
          まだ記事がありません。「新しい記事」から作成してください。
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((a) => (
            <div
              key={a.id}
              data-testid={`article-row-${a.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4 items-start"
            >
              {a.cover_image_url ? (
                <img
                  src={`${BACKEND_URL}${a.cover_image_url}`}
                  alt=""
                  className="w-24 h-24 object-cover rounded-lg shrink-0 bg-slate-100"
                />
              ) : (
                <div className="w-24 h-24 rounded-lg shrink-0 bg-emerald-50 flex items-center justify-center">
                  <BookOpen className="h-7 w-7 text-emerald-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${TYPE_META[a.type]?.badge}`}>
                    {TYPE_META[a.type]?.label}
                  </span>
                  {!a.is_published && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">非公開</span>
                  )}
                </div>
                <h3 className="font-semibold text-slate-800 mt-1 truncate">{a.title}</h3>
                <p className="text-sm text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">{a.body}</p>
                {(a.products || []).length > 0 && (
                  <div className="text-[11px] text-slate-400 mt-1">おすすめ商品 {a.products.length} 件</div>
                )}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => togglePublish(a)} data-testid={`article-publish-${a.id}`} title={a.is_published ? "非公開にする" : "公開する"}>
                  {a.is_published ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`article-edit-${a.id}`}>
                  <Edit className="h-4 w-4 text-slate-600" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(a)} data-testid={`article-delete-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "記事を編集" : "新しい記事"}</DialogTitle>
            <DialogDescription>
              キーワードを入れて「AIで記事を生成」を押すと、タイトルと本文の下書きが作られます。内容を確認・編集して保存してください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>種類</Label>
              <div className="flex gap-2 mt-1">
                {Object.entries(TYPE_META).map(([key, meta]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, type: key }))}
                    data-testid={`article-type-${key}`}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                      form.type === key ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-medium" : "border-slate-200 text-slate-600"
                    }`}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>キーワード（AI生成のヒント）</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.keywords}
                  onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                  placeholder={form.type === "feature" ? "例：夏野菜 免疫 子ども" : "例：夏 熱中症予防 水分補給"}
                  data-testid="article-keywords-input"
                  lang="ja"
                />
                <Button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                  data-testid="article-generate-btn"
                >
                  {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  AIで記事を生成
                </Button>
              </div>
            </div>

            {form.type === "feature" && (
              <div>
                <Label>おすすめ商品を選ぶ</Label>
                {selectedProducts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="article-selected-products">
                    {selectedProducts.map((p) => (
                      <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full pl-2 pr-1 py-0.5">
                        {p.name}
                        <button type="button" onClick={() => toggleProduct(p)} className="hover:bg-emerald-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative mt-1.5">
                  <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    value={prodQuery}
                    onChange={(e) => setProdQuery(e.target.value)}
                    placeholder="商品名・コードで検索して追加"
                    className="pl-8"
                    data-testid="article-product-search"
                    lang="ja"
                  />
                </div>
                {(searching || prodResults.length > 0) && (
                  <div className="mt-1 border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
                    {searching && <div className="px-3 py-2 text-xs text-slate-400">検索中...</div>}
                    {prodResults.map((p) => {
                      const sel = selectedProducts.find((x) => x.id === p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => toggleProduct(p)}
                          data-testid={`article-product-option-${p.id}`}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-50 ${sel ? "bg-emerald-50" : ""}`}
                        >
                          <span className="truncate">{p.name} <span className="text-slate-400">/ {p.unit}</span></span>
                          {sel && <span className="text-emerald-600 text-xs">選択中</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>タイトル</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="記事タイトル"
                className="mt-1"
                data-testid="article-title-input"
                lang="ja"
              />
            </div>

            <div>
              <Label>本文</Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="本文（AIで生成した内容を編集できます）"
                rows={10}
                className="mt-1"
                data-testid="article-body-input"
                lang="ja"
              />
            </div>

            <div>
              <Label>カバー画像（任意）</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.cover_image_url && (
                  <img src={`${BACKEND_URL}${form.cover_image_url}`} alt="" className="w-16 h-16 object-cover rounded-lg bg-slate-100" />
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" id="article-cover-file" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="article-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                  画像をアップロード
                </Button>
                {form.cover_image_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, cover_image_url: "" }))}>
                    削除
                  </Button>
                )}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
                data-testid="article-publish-checkbox"
              />
              施設様の画面に公開する
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="article-save-btn">
              保存
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
